/**
 * useOrgChat Hook — Org-wide AI chat state management with SSE streaming
 *
 * Generalized from useChat.ts (Chat About Candidate) but without an
 * applicationId — conversations are scoped to the whole organization
 * via /api/rb/conversations.
 */

import { useState, useCallback, useRef } from 'react';

const BASE_URL = import.meta.env.VITE_API_URL || '';

export interface OrgChatMessage {
  id: number;
  role: string;
  content: string;
}

export interface PageContext {
  type: 'candidate' | 'job';
  id: number;
}

export function useOrgChat() {
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<OrgChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const startConversation = useCallback(async (pageContext?: PageContext) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/api/rb/conversations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pageContext ? { pageContext } : {}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Failed to start conversation' }));
        throw new Error(body.error || 'Failed to start conversation');
      }
      const data = await res.json();
      setConversationId(data.id);

      // Fetch any messages the backend seeded at creation time (e.g. a
      // "since you were last here" pending-signals message) so they're
      // visible immediately rather than only appearing after the user sends
      // their own first message.
      try {
        const messagesRes = await fetch(`${BASE_URL}/api/rb/conversations/${data.id}/messages`, {
          credentials: 'include',
        });
        if (messagesRes.ok) {
          const messagesData = await messagesRes.json();
          setMessages(messagesData.messages ?? []);
        }
      } catch {
        // Non-fatal — the conversation still works, just without any
        // seeded messages pre-populated.
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start conversation';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    if (!conversationId || isStreaming) return;

    setError(null);
    setIsStreaming(true);
    setStreamingContent('');

    // Optimistically add user message
    const tempUserMsg: OrgChatMessage = { id: Date.now(), role: 'user', content };
    setMessages((prev) => [...prev, tempUserMsg]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const url = `${BASE_URL}/api/rb/conversations/${conversationId}/messages`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          try {
            const event = JSON.parse(trimmed.slice(6));

            if (event.type === 'delta' && event.content) {
              accumulated += event.content;
              setStreamingContent(accumulated);
            } else if (event.type === 'done') {
              // Finalize: copy the accumulated streaming content into the
              // committed message (matches useChat.ts's exact behavior —
              // no refetch from GET .../messages).
              const assistantMsg: OrgChatMessage = {
                id: event.messageId || Date.now() + 1,
                role: 'assistant',
                content: accumulated,
              };
              setMessages((prev) => [...prev, assistantMsg]);
              setStreamingContent('');
            } else if (event.type === 'error') {
              setError(event.error || 'AI service error');
            }
          } catch {
            // Skip unparseable lines
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        const msg = err instanceof Error ? err.message : 'Failed to send message';
        setError(msg);
      }
    } finally {
      setIsStreaming(false);
      setStreamingContent('');
      abortRef.current = null;
    }
  }, [conversationId, isStreaming]);

  return { conversationId, messages, isStreaming, streamingContent, error, isLoading, startConversation, sendMessage };
}
